import * as i18n from './rsrc/contact.i18n'

import * as _ from 'lodash'
import * as models from '../icc-api/model/models'
import { CalendarItem, User } from '../icc-api/model/models'
import { IccCryptoXApi } from './icc-crypto-x-api'
import { IccCalendarItemApi } from '../icc-api'
import { IccDataOwnerXApi } from './icc-data-owner-x-api'
import { AuthenticationProvider, NoAuthenticationProvider } from './auth/AuthenticationProvider'
import { ShareMetadataBehaviour } from './crypto/ShareMetadataBehaviour'
import { ShareResult } from './utils/ShareResult'
import { EntityShareRequest } from '../icc-api/model/requests/EntityShareRequest'
import { SecureDelegation } from '../icc-api/model/SecureDelegation'
import { XHR } from '../icc-api/api/XHR'
import { EncryptedFieldsManifest, EntityWithDelegationTypeName, parseEncryptedFields } from './utils'
import { EncryptedEntityXApi } from './basexapi/EncryptedEntityXApi'
import RequestedPermissionEnum = EntityShareRequest.RequestedPermissionEnum
import AccessLevelEnum = SecureDelegation.AccessLevelEnum
import { PaginatedListCalendarItem } from '../icc-api/model/PaginatedListCalendarItem'
import { SecretIdUseOption } from './crypto/SecretIdUseOption'

export class IccCalendarItemXApi extends IccCalendarItemApi implements EncryptedEntityXApi<models.CalendarItem> {
  i18n: any = i18n
  crypto: IccCryptoXApi
  dataOwnerApi: IccDataOwnerXApi
  private readonly encryptedFields: EncryptedFieldsManifest

  get headers(): Promise<Array<XHR.Header>> {
    return super.headers.then((h) => this.crypto.accessControlKeysHeaders.addAccessControlKeysHeaders(h, EntityWithDelegationTypeName.CalendarItem))
  }

  constructor(
    host: string,
    headers: { [key: string]: string },
    crypto: IccCryptoXApi,
    dataOwnerApi: IccDataOwnerXApi,
    private readonly autofillAuthor: boolean,
    encryptedKeys: Array<string> = ['details', 'title', 'patientId'],
    authenticationProvider: AuthenticationProvider = new NoAuthenticationProvider(),
    fetchImpl: (input: RequestInfo, init?: RequestInit) => Promise<Response> = typeof window !== 'undefined'
      ? window.fetch
      : typeof self !== 'undefined'
      ? self.fetch
      : fetch
  ) {
    super(host, headers, authenticationProvider, fetchImpl)
    this.crypto = crypto
    this.dataOwnerApi = dataOwnerApi
    this.encryptedFields = parseEncryptedFields(encryptedKeys, 'CalendarItem.')
  }

  newInstance(
    user: User,
    ci: any | CalendarItem,
    options: {
      additionalDelegates?: { [dataOwnerId: string]: AccessLevelEnum }
    } = {}
  ) {
    return this.newInstancePatient(user, null, ci, options)
  }

  /**
   * Creates a new instance of calendar item with initialised encryption metadata (not in the database).
   * @param user the current user.
   * @param patient the patient this calendar item refers to.
   * @param ci initialised data for the calendar item. Metadata such as id, creation data, etc. will be automatically initialised, but you can specify
   * other kinds of data or overwrite generated metadata with this. You can't specify encryption metadata.
   * @param options optional parameters:
   * - additionalDelegates: delegates which will have access to the entity in addition to the current data owner and delegates from the
   * auto-delegations. Must be an object which associates each data owner id with the access level to give to that data owner. May overlap with
   * auto-delegations, in such case the access level specified here will be used.
   * - preferredSfk: secret id of the patient to use as the secret foreign key to use for the classcalendar itemification. The default value will be a
   * secret id of patient known by the topmost parent in the current data owner hierarchy.
   * @return a new instance of calendar item.
   */
  async newInstancePatient(
    user: models.User,
    patient: models.Patient | null,
    ci: any,
    options: {
      additionalDelegates?: { [dataOwnerId: string]: AccessLevelEnum }
      sfkOption?: SecretIdUseOption
    } = {}
  ): Promise<models.CalendarItem> {
    const calendarItem = {
      ...(ci ?? {}),
      _type: 'org.taktik.icure.entities.CalendarItem',
      id: ci?.id ?? this.crypto.primitives.randomUuid(),
      created: ci?.created ?? new Date().getTime(),
      modified: ci?.modified ?? new Date().getTime(),
      responsible: ci?.responsible ?? (this.autofillAuthor ? this.dataOwnerApi.getDataOwnerIdOf(user) : undefined),
      author: ci?.author ?? (this.autofillAuthor ? user.id : undefined),
      codes: ci?.codes ?? [],
      tags: ci?.tags ?? [],
    }

    const ownerId = this.dataOwnerApi.getDataOwnerIdOf(user)
    if (ownerId !== (await this.dataOwnerApi.getCurrentDataOwnerId())) throw new Error('Can only initialise entities as current data owner.')
    const sfk =
      patient != undefined
        ? await this.crypto.xapi.resolveSecretIdUseOptions(
            { entity: patient, type: EntityWithDelegationTypeName.Patient },
            options.sfkOption ?? SecretIdUseOption.UseAnySharedWithParent
          )
        : undefined
    const extraDelegations = {
      ...Object.fromEntries(
        [...(user.autoDelegations?.all ?? []), ...(user.autoDelegations?.medicalInformation ?? [])].map((d) => [d, AccessLevelEnum.WRITE])
      ),
      ...(options?.additionalDelegates ?? {}),
    }
    return new CalendarItem(
      await this.crypto.xapi
        .entityWithInitialisedEncryptedMetadata(calendarItem, EntityWithDelegationTypeName.CalendarItem, patient?.id, sfk, true, extraDelegations)
        .then((x) => x.updatedEntity)
    )
  }

  /**
   * @deprecated use {@link findIdsBy} instead.
   */
  async findBy(hcpartyId: string, patient: models.Patient, usingPost: boolean = false) {
    const extractedKeys = await this.crypto.xapi.secretIdsOf({ entity: patient, type: EntityWithDelegationTypeName.Patient }, hcpartyId)
    const topmostParentId = (await this.dataOwnerApi.getCurrentDataOwnerHierarchyIds())[0]
    return extractedKeys && extractedKeys.length > 0
      ? usingPost
        ? this.findByHCPartyPatientSecretFKeysArray(hcpartyId!, _.uniq(extractedKeys))
        : this.findByHCPartyPatientSecretFKeys(hcpartyId!, _.uniq(extractedKeys).join(','))
      : Promise.resolve([])
  }

  /**
   * Same as `findBy` but it will only return the ids of the calendar items. It can also filter the access logs where CalendarItem.date is between
   * startDate and endDate in ascending or descending order by that field. (default: ascending).
   */
  async findIdsBy(hcpartyId: string, patient: models.Patient, startDate?: number, endDate?: number, descending?: boolean): Promise<string[]> {
    const extractedKeys = await this.crypto.xapi.secretIdsOf({ entity: patient, type: EntityWithDelegationTypeName.Patient }, hcpartyId)
    return extractedKeys && extractedKeys.length > 0
      ? this.findCalendarItemIdsByDataOwnerPatientStartTime(hcpartyId, _.uniq(extractedKeys), startDate, endDate, descending)
      : Promise.resolve([])
  }

  /**
   * @deprecated use {@link findCalendarItemIdsByDataOwnerPatientStartTime} instead.
   */
  async findByHCPartyPatientSecretFKeys(hcPartyId: string, secretFKeys: string): Promise<Array<models.CalendarItem>> {
    const calendarItems = await super.findCalendarItemsByHCPartyPatientForeignKeys(hcPartyId, secretFKeys)
    return await this.decrypt(hcPartyId, calendarItems)
  }

  /**
   * @deprecated use {@link findCalendarItemIdsByDataOwnerPatientStartTime} instead.
   */
  findByHCPartyPatientSecretFKeysArray(hcPartyId: string, secretFKeys: string[]): Promise<Array<models.CalendarItem> | any> {
    return super
      .findCalendarItemsByHCPartyPatientForeignKeysUsingPost(hcPartyId, secretFKeys)
      .then((calendarItems) => this.decrypt(hcPartyId, calendarItems))
  }

  createCalendarItem(body?: CalendarItem): never {
    throw new Error('Cannot call a method that must encrypt a calendar item without providing a user for de/encryption')
  }

  async createCalendarItemWithHcParty(user: models.User, body?: models.CalendarItem): Promise<models.CalendarItem | any> {
    return body
      ? this.encrypt(user, [_.cloneDeep(body)])
          .then((items) => super.createCalendarItem(items[0]))
          .then((ci) => this.decrypt(this.dataOwnerApi.getDataOwnerIdOf(user)!, [ci]))
          .then((cis) => cis[0])
      : null
  }

  getCalendarItemWithUser(user: models.User, calendarItemId: string): Promise<CalendarItem | any> {
    return super
      .getCalendarItem(calendarItemId)
      .then((calendarItem) => this.decrypt(this.dataOwnerApi.getDataOwnerIdOf(user)!, [calendarItem]))
      .then((cis) => cis[0])
  }

  getCalendarItem(calendarItemId: string): never {
    throw new Error('Cannot call a method that must en/decrypt a calendar item without providing a user for de/encryption')
  }

  /**
   * @deprecated use {@link getCalendarItemsWithPaginationWithUser} instead.
   */
  async getCalendarItemsWithUser(user: models.User): Promise<Array<CalendarItem> | any> {
    return super.getCalendarItems().then((calendarItems) => this.decrypt(this.dataOwnerApi.getDataOwnerIdOf(user)!, calendarItems))
  }

  getCalendarItems(): never {
    throw new Error('Cannot call a method that must en/decrypt a calendar item without providing a user for de/encryption')
  }

  async getCalendarItemsWithPaginationWithUser(user: models.User, startDocumentId?: string, limit?: number): Promise<PaginatedListCalendarItem> {
    return super.getCalendarItemsWithPagination(startDocumentId, limit).then((calendarItems) =>
      this.decrypt(this.dataOwnerApi.getDataOwnerIdOf(user)!, calendarItems.rows ?? []).then(
        (decryptedItems) =>
          new PaginatedListCalendarItem({
            rows: decryptedItems,
            nextKeyPair: calendarItems.nextKeyPair,
          })
      )
    )
  }

  getCalendarItemsWithPagination(startDocumentId?: string, limit?: number): never {
    throw new Error('Cannot call a method that must en/decrypt a calendar item without providing a user for de/encryption')
  }

  /**
   * @deprecated use {@link findCalendarItemsByRecurrenceIdWithPaginationWithUser} instead.
   */
  async findCalendarItemsByRecurrenceIdWithUser(user: models.User, recurrenceId: string): Promise<Array<CalendarItem> | any> {
    return super
      .findCalendarItemsByRecurrenceId(recurrenceId)
      .then((calendarItems) => this.decrypt(this.dataOwnerApi.getDataOwnerIdOf(user)!, calendarItems))
  }

  findCalendarItemsByRecurrenceId(recurrenceId: string): never {
    throw new Error('Cannot call a method that must en/decrypt a calendar item without providing a user for de/encryption')
  }

  async findCalendarItemsByRecurrenceIdWithPaginationWithUser(
    user: models.User,
    recurrenceId: string,
    startKey?: string,
    startDocumentId?: string,
    limit?: number
  ): Promise<PaginatedListCalendarItem> {
    return super.findCalendarItemsByRecurrenceIdWithPagination(recurrenceId, startKey, startDocumentId, limit).then((calendarItems) =>
      this.decrypt(this.dataOwnerApi.getDataOwnerIdOf(user)!, calendarItems.rows ?? []).then(
        (decryptedItems) =>
          new PaginatedListCalendarItem({
            rows: decryptedItems,
            nextKeyPair: calendarItems.nextKeyPair,
          })
      )
    )
  }

  findCalendarItemsByRecurrenceIdWithPagination(recurrenceId: string, startKey?: string, startDocumentId?: string, limit?: number): never {
    throw new Error('Cannot call a method that must en/decrypt a calendar item without providing a user for de/encryption')
  }

  getCalendarItemsWithIdsWithUser(user: models.User, body?: models.ListOfIds): Promise<Array<CalendarItem> | any> {
    return super.getCalendarItemsWithIds(body).then((calendarItems) => this.decrypt(this.dataOwnerApi.getDataOwnerIdOf(user)!, calendarItems))
  }

  getCalendarItemsWithIds(body?: models.ListOfIds): never {
    throw new Error('Cannot call a method that must en/decrypt a calendar item without providing a user for de/encryption')
  }

  getCalendarItemsByPeriodAndHcPartyIdWithUser(
    user: models.User,
    startDate: number,
    endDate: number,
    hcPartyId: string
  ): Promise<Array<CalendarItem> | any> {
    return super
      .getCalendarItemsByPeriodAndHcPartyId(startDate, endDate, hcPartyId)
      .then((calendarItems) => this.decrypt(this.dataOwnerApi.getDataOwnerIdOf(user)!, calendarItems))
  }

  getCalendarItemsByPeriodAndHcPartyId(startDate?: number, endDate?: number, hcPartyId?: string): never {
    throw new Error('Cannot call a method that must en/decrypt a calendar item without providing a user for de/encryption')
  }

  getCalendarsByPeriodAndAgendaIdWithUser(
    user: models.User,
    startDate: number,
    endDate: number,
    agendaId: string
  ): Promise<Array<CalendarItem> | any> {
    return super
      .getCalendarsByPeriodAndAgendaId(startDate, endDate, agendaId)
      .then((calendarItems) => this.decrypt(this.dataOwnerApi.getDataOwnerIdOf(user)!, calendarItems))
  }

  getCalendarsByPeriodAndAgendaId(startDate?: number, endDate?: number, agendaId?: string): never {
    throw new Error('Cannot call a method that must en/decrypt a calendar item without providing a user for de/encryption')
  }

  modifyCalendarItem(body?: CalendarItem): never {
    throw new Error('Cannot call a method that must encrypt a calendar item without providing a user for de/encryption')
  }

  /**
   * Remove the following delegation objects from the
   * CalendarItem instance: cryptedForeignKeys, secretForeignKeys.
   *
   * The delegations & encryptionKeys objects are not removed because
   * in the case the CalendarItem is saved in the DB & then encrypted,
   * if later we remove the patient from it, it'd reset the delegations
   * and encryptionKeys thus impossibilitating further access.
   *
   * @param calendarItem The Calendar Item object
   */
  resetCalendarDelegationObjects(calendarItem: models.CalendarItem): models.CalendarItem {
    const { cryptedForeignKeys, secretForeignKeys, ...resetCalendarItem } = calendarItem
    return resetCalendarItem
  }

  async modifyCalendarItemWithHcParty(user: models.User, body?: models.CalendarItem): Promise<models.CalendarItem | any> {
    return body ? this.modifyAs(this.dataOwnerApi.getDataOwnerIdOf(user)!, _.cloneDeep(body)) : null
  }

  private modifyAs(dataOwner: string, body: models.CalendarItem): Promise<models.CalendarItem> {
    return this.encryptAs(dataOwner, [_.cloneDeep(body)])
      .then((items) => super.modifyCalendarItem(items[0]))
      .then((ci) => this.decrypt(dataOwner, [ci]))
      .then((cis) => cis[0])
  }

  encrypt(user: models.User, calendarItems: Array<models.CalendarItem>): Promise<Array<models.CalendarItem>> {
    return this.encryptAs(this.dataOwnerApi.getDataOwnerIdOf(user)!, calendarItems)
  }

  private encryptAs(dataOwner: string, calendarItems: Array<models.CalendarItem>): Promise<Array<models.CalendarItem>> {
    return this.crypto.xapi.tryEncryptEntities(
      calendarItems,
      EntityWithDelegationTypeName.CalendarItem,
      this.encryptedFields,
      false,
      false,
      (json) => new CalendarItem(json)
    )
  }

  async decrypt(hcpId: string, calendarItems: Array<models.CalendarItem>): Promise<Array<models.CalendarItem>> {
    return (
      await this.crypto.xapi.tryDecryptEntities(calendarItems, EntityWithDelegationTypeName.CalendarItem, (json) => new CalendarItem(json))
    ).map(({ entity }) => entity)
  }

  /**
   * @param calendarItem a calendar item
   * @return the id of the patient that the calendar item refers to, retrieved from the encrypted metadata. Normally there should only be one element
   * in the returned array, but in case of entity merges there could be multiple values.
   */
  async decryptPatientIdOf(calendarItem: models.CalendarItem): Promise<string[]> {
    return this.crypto.xapi.owningEntityIdsOf({ entity: calendarItem, type: EntityWithDelegationTypeName.CalendarItem }, undefined)
  }

  /**
   * @return if the logged data owner has write access to the content of the given calendar item
   */
  async hasWriteAccess(calendarItem: models.CalendarItem): Promise<boolean> {
    return this.crypto.xapi.hasWriteAccess({ entity: calendarItem, type: EntityWithDelegationTypeName.CalendarItem })
  }

  /**
   * Share an existing calendar item with other data owners, allowing them to access the non-encrypted data of the calendar item and optionally also
   * the encrypted content, with read-only or read-write permissions.
   * @param delegateId the id of the data owner which will be granted access to the calendar item.
   * @param calendarItem item the calendar item to share.
   * @param options optional parameters to customize the sharing behaviour:
   * - shareSecretIds: specifies which secret ids of the entity should be shared. If not provided all secret ids available to the current user will be shared
   * - shareEncryptionKey: specifies if the encryption key of the access log should be shared with the delegate, giving access to all encrypted
   * content of the entity, excluding other encrypted metadata (defaults to {@link ShareMetadataBehaviour.IF_AVAILABLE}). Note that by default a
   * calendar item does not have encrypted content.
   * - sharePatientId: specifies if the id of the patient that this calendar item refers to should be shared with the delegate (defaults to
   * {@link ShareMetadataBehaviour.IF_AVAILABLE}).
   * - requestedPermissions: the requested permissions for the delegate, defaults to {@link RequestedPermissionEnum.MAX_WRITE}.
   * @return a promise which will contain the result of the operation: the updated entity if the operation was successful or details of the error if
   * the operation failed.
   */
  async shareWith(
    delegateId: string,
    calendarItem: models.CalendarItem,
    options: {
      shareSecretIds?: string[]
      requestedPermissions?: RequestedPermissionEnum
      shareEncryptionKey?: ShareMetadataBehaviour // Defaults to ShareMetadataBehaviour.IF_AVAILABLE
      sharePatientId?: ShareMetadataBehaviour // Defaults to ShareMetadataBehaviour.IF_AVAILABLE
    } = {}
  ): Promise<models.CalendarItem> {
    return this.shareWithMany(calendarItem, { [delegateId]: options })
  }

  /**
   * Share an existing calendar item with other data owners, allowing them to access the non-encrypted data of the calendar item and optionally also
   * the encrypted content, with read-only or read-write permissions.
   * @param calendarItem item the calendar item to share.
   * @param delegates associates the id of data owners which will be granted access to the entity, to the following sharing options:
   * - shareSecretIds: specifies which secret ids of the entity should be shared. If not provided all secret ids available to the current user will be shared
   * - shareEncryptionKey: specifies if the encryption key of the access log should be shared with the delegate, giving access to all encrypted
   * content of the entity, excluding other encrypted metadata (defaults to {@link ShareMetadataBehaviour.IF_AVAILABLE}). Note that by default a
   * calendar item does not have encrypted content.
   * - sharePatientId: specifies if the id of the patient that this calendar item refers to should be shared with the delegate (defaults to
   * {@link ShareMetadataBehaviour.IF_AVAILABLE}).
   * - requestedPermissions: the requested permissions for the delegate, defaults to {@link RequestedPermissionEnum.MAX_WRITE}.
   * @return the updated entity
   */
  async shareWithMany(
    calendarItem: models.CalendarItem,
    delegates: {
      [delegateId: string]: {
        shareSecretIds?: string[]
        requestedPermissions?: RequestedPermissionEnum
        shareEncryptionKey?: ShareMetadataBehaviour // Defaults to ShareMetadataBehaviour.IF_AVAILABLE
        sharePatientId?: ShareMetadataBehaviour // Defaults to ShareMetadataBehaviour.IF_AVAILABLE
      }
    }
  ): Promise<models.CalendarItem> {
    return (await this.tryShareWithMany(calendarItem, delegates)).updatedEntityOrThrow
  }

  /**
   * Share an existing calendar item with other data owners, allowing them to access the non-encrypted data of the calendar item and optionally also
   * the encrypted content, with read-only or read-write permissions.
   * @param calendarItem item the calendar item to share.
   * @param delegates associates the id of data owners which will be granted access to the entity, to the following sharing options:
   * - shareSecretIds: specifies which secret ids of the entity should be shared. If not provided all secret ids available to the current user will be shared
   * - shareEncryptionKey: specifies if the encryption key of the access log should be shared with the delegate, giving access to all encrypted
   * content of the entity, excluding other encrypted metadata (defaults to {@link ShareMetadataBehaviour.IF_AVAILABLE}). Note that by default a
   * calendar item does not have encrypted content.
   * - sharePatientId: specifies if the id of the patient that this calendar item refers to should be shared with the delegate (defaults to
   * {@link ShareMetadataBehaviour.IF_AVAILABLE}).
   * - requestedPermissions: the requested permissions for the delegate, defaults to {@link RequestedPermissionEnum.MAX_WRITE}.
   * @return the updated entity
   */
  async tryShareWithMany(
    calendarItem: models.CalendarItem,
    delegates: {
      [delegateId: string]: {
        shareSecretIds?: string[]
        requestedPermissions?: RequestedPermissionEnum
        shareEncryptionKey?: ShareMetadataBehaviour // Defaults to ShareMetadataBehaviour.IF_AVAILABLE
        sharePatientId?: ShareMetadataBehaviour // Defaults to ShareMetadataBehaviour.IF_AVAILABLE
      }
    }
  ): Promise<ShareResult<models.CalendarItem>> {
    const self = await this.dataOwnerApi.getCurrentDataOwnerId()
    // All entities should have an encryption key.
    const entityWithEncryptionKey = await this.crypto.xapi.ensureEncryptionKeysInitialised(calendarItem, EntityWithDelegationTypeName.CalendarItem)
    const updatedEntity = entityWithEncryptionKey ? await this.modifyAs(self, entityWithEncryptionKey) : calendarItem
    return this.crypto.xapi
      .simpleShareOrUpdateEncryptedEntityMetadata(
        {
          entity: updatedEntity,
          type: EntityWithDelegationTypeName.CalendarItem,
        },
        Object.fromEntries(
          Object.entries(delegates).map(([delegateId, options]) => [
            delegateId,
            {
              requestedPermissions: options.requestedPermissions,
              shareEncryptionKeys: options.shareEncryptionKey,
              shareOwningEntityIds: options.sharePatientId,
              shareSecretIds: options.shareSecretIds,
            },
          ])
        ),
        (x) => this.bulkShareCalendarItems(x)
      )
      .then((r) => r.mapSuccessAsync((e) => this.decrypt(self, [e]).then((es) => es[0])))
  }

  getDataOwnersWithAccessTo(
    entity: CalendarItem
  ): Promise<{ permissionsByDataOwnerId: { [p: string]: AccessLevelEnum }; hasUnknownAnonymousDataOwners: boolean }> {
    return this.crypto.delegationsDeAnonymization.getDataOwnersWithAccessTo({ entity, type: EntityWithDelegationTypeName.CalendarItem })
  }

  getEncryptionKeysOf(entity: CalendarItem): Promise<string[]> {
    return this.crypto.xapi.encryptionKeysOf({ entity, type: EntityWithDelegationTypeName.CalendarItem }, undefined)
  }

  /**
   * Adds a bidirectional link between a calendar item with a patient.
   * Note that this operation is not reversible: it is not possible to change the patient linked to a calendar
   * item.
   * @param calendarItem a calendar item
   * @param patient the patient which will be linked to the calendar item.
   * @param shareLinkWithDelegates data owners other than the current data owner which will also be able to decrypt the id of the newly linked
   * patient. If any of these data owners do not already have access to the calendar item, they will be granted read access (no write).
   * @param sfkOption specify how to create the link from the patient
   * @return the updated calendar item
   */
  async linkWithPatient(
    calendarItem: models.CalendarItem,
    patient: models.Patient,
    shareLinkWithDelegates: string[],
    sfkOption?: SecretIdUseOption
  ): Promise<models.CalendarItem> {
    if (this.hasLinkFromPatient(calendarItem)) throw new Error(`Calendar item ${calendarItem.id} is already linked to a patient`)
    const delegates = [...new Set([await this.dataOwnerApi.getCurrentDataOwnerId(), ...shareLinkWithDelegates])]
    const sfk = await this.crypto.xapi.resolveSecretIdUseOptions(
      { entity: patient, type: EntityWithDelegationTypeName.Patient },
      sfkOption ?? SecretIdUseOption.UseAnySharedWithParent
    )
    const individualShareData = {
      shareSecretIds: [] as string[],
      shareEncryptionKeys: [] as string[],
      shareOwningEntityIds: [patient.id!],
      requestedPermissions: RequestedPermissionEnum.FULL_READ,
    }
    const shared = await this.crypto.xapi.bulkShareOrUpdateEncryptedEntityMetadata(
      EntityWithDelegationTypeName.CalendarItem,
      [
        {
          entity: calendarItem,
          dataForDelegates: Object.fromEntries(delegates.map((d) => [d, individualShareData])),
        },
      ],
      (x) => this.bulkShareCalendarItems(x)
    )
    if (!shared.updatedEntities.length || shared.updatedEntities[0].id !== calendarItem.id) {
      const errorsForEntity = shared.updateErrors.filter((e) => e.entityId === calendarItem.id)
      if (!errorsForEntity.length || !errorsForEntity.find((x) => x.code === 409)) {
        throw new Error(`Unexpected error while linking calendar item ${calendarItem.id}`)
      } else {
        throw new Error(`Outdated calendar item revision ${calendarItem.id}-${calendarItem.rev}`)
      }
    }
    const self = await this.dataOwnerApi.getCurrentDataOwnerId()
    const sharedDecrypted = (await this.decrypt(self, [shared.updatedEntities[0]]))[0]
    if (sfk.length > 0) {
      return await this.modifyAs(self, { ...sharedDecrypted, secretForeignKeys: sfk })
    } else {
      return sharedDecrypted
    }
  }

  /**
   * Checks if a calendar item has an (encrypted) link from a patient.
   * Calendar item where this method returns false won't be findable when searching for them by patient.
   * If this method returns false there may still be a link to the patient; you can check for it by using the method
   * {@link decryptPatientIdOf}.
   *
   * Note: this method only checks the presence of a link, it doesn't verify that it is valid (for example, this method
   * doesn't check if the link from the patient matches the link to the patient).
   */
  hasLinkFromPatient(calendarItem: CalendarItem): boolean {
    return (calendarItem.secretForeignKeys ?? []).length > 0
  }

  /**
   * Adds a link to a calendar item allowing it to be found from the patient.
   * This method only adds a partial link; if the calendar item is not already linked to the patient you should
   * instead use the {@link linkWithPatient}
   * @param calendarItem the calendar item to update.
   * @param patient the patient to link with the calendar item.
   * @param sfkOption customize how to create the link.
   * @return the updated calendar item
   */
  async addLinkFromPatient(calendarItem: CalendarItem, patient: models.Patient, sfkOption?: SecretIdUseOption): Promise<CalendarItem> {
    if (this.hasLinkFromPatient(calendarItem)) throw new Error(`Calendar item ${calendarItem.id} is already linked to a patient`)
    const self = await this.dataOwnerApi.getCurrentDataOwnerId()
    const sfk = await this.crypto.xapi.resolveSecretIdUseOptions(
      { entity: patient, type: EntityWithDelegationTypeName.Patient },
      sfkOption ?? SecretIdUseOption.UseAnySharedWithParent
    )
    if (sfk.length > 0) {
      return await this.modifyAs(self, { ...calendarItem, secretForeignKeys: sfk })
    } else {
      return calendarItem
    }
  }

  createDelegationDeAnonymizationMetadata(entity: CalendarItem, delegates: string[]): Promise<void> {
    return this.crypto.delegationsDeAnonymization.createOrUpdateDeAnonymizationInfo(
      { entity, type: EntityWithDelegationTypeName.CalendarItem },
      delegates
    )
  }
}
